"use client";

import { useState } from "react";
import { useHeroMapContext } from "@/components/home/context/HeroMapContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useListingBrowseContext } from "../context/listing-browse-context";
import {
  PhoneConditionSection,
  PhoneListingSection,
  PhoneLocationSection,
  PhoneProductSection,
  PhoneSpecSection,
} from "./phone-filter-sections";

export function usePhoneFilterCount(): number {
  const browse = useListingBrowseContext();
  const { location, quantity } = useHeroMapContext();
  const searchCount = browse.searchQuery.length > 0 ? 1 : 0;
  const productCount = browse.productType && !browse.lockedProductType ? 1 : 0;
  return (
    browse.activeFilterCount -
    searchCount +
    productCount +
    (location ? 1 : 0) +
    (quantity > 1 ? 1 : 0)
  );
}

function useResetPhoneFilters() {
  const browse = useListingBrowseContext();
  const { setLocation, setQuantity } = useHeroMapContext();
  return () => {
    browse.setConditions([]);
    browse.setPriceRange({ min: null, max: null });
    browse.setSpecFilters([]);
    browse.setStateCode(null);
    browse.setListedAfterDays(null);
    browse.setIntentFilter("all");
    if (!browse.lockedProductType) browse.setProductType(null);
    setLocation(null);
    setQuantity(1);
  };
}

export function PhoneFilterSheet({
  open,
  onOpenChange,
  resultCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resultCount: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const close = () => onOpenChange(false);
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setExpanded(false);
      }}
    >
      <SheetContent
        side="bottom"
        layout="grouped"
        showCloseButton={false}
        className={cn(
          "h-[62dvh] pb-0 transition-[height] duration-base md:hidden",
          expanded && "h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)]",
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded((previous) => !previous)}
          aria-label={expanded ? "Show half height" : "Show full height"}
          aria-pressed={expanded}
          className="absolute inset-x-0 top-0 z-10 h-6 rounded-t-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <PhoneFilterSheetHeader onDone={close} />
        <SheetBody className="space-y-5 pb-4">
          <PhoneProductSection />
          <PhoneLocationSection />
          <PhoneListingSection />
          <PhoneConditionSection />
          <PhoneSpecSection />
        </SheetBody>
        <PhoneFilterSheetFooter resultCount={resultCount} onDone={close} />
      </SheetContent>
    </Sheet>
  );
}

function PhoneFilterSheetHeader({ onDone }: { onDone: () => void }) {
  const count = usePhoneFilterCount();
  const reset = useResetPhoneFilters();
  return (
    <SheetHeader>
      <Button
        type="button"
        variant="quiet"
        size="sm"
        onClick={reset}
        disabled={count === 0}
        className="-ml-3 text-action"
      >
        Reset
      </Button>
      <SheetTitle>Filters</SheetTitle>
      <SheetDescription className="sr-only">
        Narrow the marketplace listings.
      </SheetDescription>
      <Button
        type="button"
        variant="quiet"
        size="sm"
        onClick={onDone}
        className="-mr-3 font-semibold text-action"
      >
        Done
      </Button>
    </SheetHeader>
  );
}

function PhoneFilterSheetFooter({
  resultCount,
  onDone,
}: {
  resultCount: number | null;
  onDone: () => void;
}) {
  return (
    <SheetFooter className="-mx-4 border-t border-border bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
      <Button
        type="button"
        variant="primary"
        size="default"
        onClick={onDone}
        className="w-full"
      >
        {resultCount === null
          ? "Show listings"
          : `Show ${resultCount.toLocaleString("en-US")} listings`}
      </Button>
    </SheetFooter>
  );
}
